/**
 * CustomerPortal.js
 * External customer-facing portal.
 * Accessed via URL hash: #customer-portal  (login)
 *                         #customer-reset?token=XXX  (password reset)
 *
 * Completely independent from the internal app auth — uses /customer/* routes.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import {
  LogIn, LogOut, Bell, Check, X, ChevronRight, ChevronDown, ChevronUp,
  Inbox, Eye, EyeOff, AlertCircle, CheckCircle, Clock, PauseCircle,
  Briefcase, User, Mail, Lock, RefreshCw, Send, Search
} from 'lucide-react';

// ── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_LABELS = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  on_hold: 'On Hold',
  completed: 'Completed',
  rejected: 'Rejected',
};

const DEFAULT_COLORS = {
  not_started: '#6b7280',
  in_progress: '#3b82f6',
  on_hold: '#f59e0b',
  completed: '#10b981',
  rejected: '#ef4444',
};

const EVENT_ICONS = {
  stage_change: <ChevronRight className="w-4 h-4 text-blue-500" />,
  completed: <CheckCircle className="w-4 h-4 text-green-500" />,
  on_hold: <PauseCircle className="w-4 h-4 text-amber-500" />,
  approval_waiting: <Clock className="w-4 h-4 text-orange-500" />,
  approval_result: <Check className="w-4 h-4 text-purple-500" />,
};

const TASK_STATUS_ICON = {
  not_started: <Clock className="w-4 h-4 text-gray-400" />,
  in_progress: <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />,
  on_hold: <PauseCircle className="w-4 h-4 text-amber-500" />,
  completed: <CheckCircle className="w-4 h-4 text-green-500" />,
  rejected: <X className="w-4 h-4 text-red-500" />,
};

function StatusBadge({ status, colors }) {
  const col = (colors || DEFAULT_COLORS)[status] || '#6b7280';
  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: col + '22', color: col, border: `1px solid ${col}55` }}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export default function CustomerPortal({ onLogout: externalOnLogout }) {
  const [view, setView] = useState('login'); // login | forgot | reset | dashboard | project | notifications
  const [token, setToken] = useState(() => localStorage.getItem('cp_token') || '');
  const [profile, setProfile] = useState(null);
  const [portalColors, setPortalColors] = useState(DEFAULT_COLORS);
  const [resetToken, setResetToken] = useState('');

  // On mount: check for #customer-reset?token=... in hash
  useEffect(() => {
    const hash = window.location.hash; // e.g. #customer-reset?token=abc123
    const resetMatch = hash.match(/customer-reset\?token=([^&]+)/);
    if (resetMatch) {
      setResetToken(decodeURIComponent(resetMatch[1]));
      setView('reset');
    }
    if (token) setView('dashboard');
  }, []); // eslint-disable-line

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const handleLogout = () => {
    localStorage.removeItem('cp_token');
    setToken('');
    setProfile(null);
    setView('login');
    if (externalOnLogout) externalOnLogout();
  };

  // ── Sub-views ──────────────────────────────────────────────────────────────

  if (view === 'reset') return <ResetPasswordView resetToken={resetToken} onDone={() => setView('login')} />;

  if (!token || view === 'login') {
    return (
      <LoginView
        onLogin={(jwt, prof, colors) => {
          localStorage.setItem('cp_token', jwt);
          setToken(jwt);
          setProfile(prof);
          setPortalColors(colors || DEFAULT_COLORS);
          setView('dashboard');
        }}
        onForgot={() => setView('forgot')}
      />
    );
  }

  if (view === 'forgot') return <ForgotPasswordView onBack={() => setView('login')} />;

  return (
    <PortalShell
      profile={profile}
      setProfile={setProfile}
      authHeaders={authHeaders}
      portalColors={portalColors}
      onLogout={handleLogout}
      initialView={view}
    />
  );
}

// ── Login ────────────────────────────────────────────────────────────────────

function LoginView({ onLogin, onForgot }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/customer/login', { email, password });
      const { access_token, profile, portal_settings } = res.data;
      onLogin(access_token, profile, portal_settings?.status_colors);
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#01158a' }}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
        <div className="flex justify-center items-center rounded-xl p-4 mb-6" style={{ backgroundColor: '#01158a' }}>
          <img src="/images/Logo-01.png" alt="VeePee Graphics" className="h-16 w-auto" />
        </div>
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold text-gray-800">Customer Portal</h1>
          <p className="text-gray-500 text-sm mt-1">Sign in to view your projects</p>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-lg px-4 py-3 mb-4 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="you@company.com"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type={showPwd ? 'text' : 'password'}
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-lg pl-10 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="••••••••"
              />
              <button type="button" onClick={() => setShowPwd(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            <LogIn className="w-4 h-4" />
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <button onClick={onForgot} className="mt-4 w-full text-center text-sm text-blue-600 hover:underline">
          Forgot password?
        </button>
      </div>
    </div>
  );
}

// ── Forgot Password ───────────────────────────────────────────────────────────

function ForgotPasswordView({ onBack }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/customer/forgot-password', { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset email.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
        <button onClick={onBack} className="text-blue-600 text-sm hover:underline mb-6 flex items-center gap-1">
          ← Back to login
        </button>
        <h2 className="text-xl font-bold text-gray-800 mb-2">Reset Password</h2>
        <p className="text-sm text-gray-500 mb-6">Enter your email and we'll send you a reset link.</p>

        {sent ? (
          <div className="flex flex-col items-center gap-3 py-4">
            <CheckCircle className="w-12 h-12 text-green-500" />
            <p className="text-sm text-gray-600 text-center">
              If an account exists for <strong>{email}</strong>, a reset link has been sent.
            </p>
            <button onClick={onBack} className="mt-2 text-blue-600 text-sm hover:underline">Back to login</button>
          </div>
        ) : (
          <>
            {error && (
              <div className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-lg px-4 py-3 mb-4 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="you@company.com"
                />
              </div>
              <button type="submit" disabled={loading} className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
                <Send className="w-4 h-4" /> {loading ? 'Sending…' : 'Send Reset Link'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// ── Reset Password ────────────────────────────────────────────────────────────

function ResetPasswordView({ resetToken, onDone }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    try {
      await api.post('/customer/reset-password', { token: resetToken, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
        <h2 className="text-xl font-bold text-gray-800 mb-2">Set New Password</h2>
        {success ? (
          <div className="flex flex-col items-center gap-3 py-4">
            <CheckCircle className="w-12 h-12 text-green-500" />
            <p className="text-sm text-gray-600 text-center">Password updated successfully.</p>
            <button onClick={onDone} className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">Back to Login</button>
          </div>
        ) : (
          <>
            {error && (
              <div className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-lg px-4 py-3 mb-4 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">New Password</label>
                <input type="password" required value={password} onChange={e => setPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="••••••••" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Confirm Password</label>
                <input type="password" required value={confirm} onChange={e => setConfirm(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="••••••••" />
              </div>
              <button type="submit" disabled={loading} className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
                {loading ? 'Updating…' : 'Update Password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// ── Portal Shell (authenticated) ──────────────────────────────────────────────

function PortalShell({ profile, setProfile, authHeaders, portalColors, onLogout, initialView }) {
  const [activeView, setActiveView] = useState(initialView === 'notifications' ? 'notifications' : 'projects');
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch profile if not loaded
  useEffect(() => {
    if (!profile) {
      api.get('/customer/profile', authHeaders())
        .then(res => setProfile(res.data))
        .catch(() => {});
    }
  }, [profile, setProfile, authHeaders]);

  // Poll unread count
  const refreshUnread = useCallback(async () => {
    try {
      const res = await api.get('/customer/notifications?unread_only=true', authHeaders());
      setUnreadCount((Array.isArray(res.data) ? res.data : []).length);
    } catch { /* ignore */ }
  }, [authHeaders]);

  useEffect(() => {
    refreshUnread();
    const t = setInterval(refreshUnread, 60000);
    return () => clearInterval(t);
  }, [refreshUnread]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top nav */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#be161b] rounded-xl flex items-center justify-center">
              <span className="w-5 h-6 font-bold text-white">VP </span>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#be161b] leading-none">VeePee Graphics - Customer Portal</p>
              {profile?.client_name && <p className="text-xs text-gray-400 mt-0.5">{profile.client_name}</p>}
            </div>
          </div>
          <nav className="flex items-center gap-2">
            <button
              onClick={() => setActiveView('projects')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${activeView === 'projects' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:text-gray-800'}`}
            >
              Projects
            </button>
            <button
              onClick={() => { setActiveView('notifications'); refreshUnread(); }}
              className={`relative px-3 py-1.5 rounded-lg text-sm font-medium transition ${activeView === 'notifications' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:text-gray-800'}`}
            >
              <Bell className="w-4 h-4 inline mr-1" />
              Inbox
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center leading-none">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            <button
              onClick={onLogout}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </nav>
        </div>
      </header>

      <main className="flex-1 max-w mx-auto w-full px-4 py-6">
        {activeView === 'projects' && !selectedProjectId && (
          <ProjectListView
            authHeaders={authHeaders}
            colors={portalColors}
            onSelectProject={id => { setSelectedProjectId(id); setActiveView('project-detail'); }}
          />
        )}
        {activeView === 'project-detail' && selectedProjectId && (
          <ProjectDetailView
            projectId={selectedProjectId}
            authHeaders={authHeaders}
            colors={portalColors}
            onBack={() => { setSelectedProjectId(null); setActiveView('projects'); }}
            onGoToNotifications={() => { setSelectedProjectId(null); setActiveView('notifications'); refreshUnread(); }}
          />
        )}
        {activeView === 'notifications' && (
          <NotificationsView
            authHeaders={authHeaders}
            colors={portalColors}
            onRefreshUnread={refreshUnread}
          />
        )}
      </main>
    </div>
  );
}

// ── Customer-visible status config ────────────────────────────────────────────

const CUSTOMER_STATUS = {
  in_progress: { label: 'In Process',        color: '#f97316' },
  paused:      { label: 'In Process',        color: '#f97316' },
  on_hold:     { label: 'On Hold',           color: '#dc2626' },
  query:       { label: 'On Hold',           color: '#dc2626' },
  not_started: { label: 'Pending',           color: '#6b7280' },
  pending:     { label: 'Pending',           color: '#6b7280' },
  completed:   { label: 'Completed',         color: '#15803d' },
  rejected:    { label: 'Rejected',          color: '#991b1b' },
};
const ACTIVE_SET = new Set(['in_progress', 'on_hold', 'query', 'paused', 'not_started', 'pending']);

function getProjectStatusDisplay(proj) {
  if ((proj.pending_approvals || []).length > 0)
    return { label: 'Sent for Approval', color: '#0ea5e9' };
  return CUSTOMER_STATUS[proj.status] || { label: proj.status || '—', color: '#6b7280' };
}

// ── Project List ──────────────────────────────────────────────────────────────

const TAB_PAGE_SIZE = 20;

function ProjectListView({ authHeaders }) {
  const [projects, setProjects]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');
  const [search, setSearch]               = useState('');
  const debounceRef                        = useRef(null);

  // Tabs + per-tab pagination
  const [activeTab, setActiveTab]         = useState('approval');
  const [tabPage, setTabPage]             = useState(1);

  // Approval modal state
  const [approvalModal, setApprovalModal] = useState(null);
  const [correctionFrom, setCorrectionFrom] = useState('Customer');
  const [remarks, setRemarks]             = useState('');
  const [modalError, setModalError]       = useState('');
  const [modalLoading, setModalLoading]   = useState(false);

  // Reminder popup
  const [reminderOpen, setReminderOpen]   = useState(false);
  const [overdueList, setOverdueList]     = useState([]);
  const [dismissed, setDismissed]         = useState(new Set());
  const reminderTimerRef                   = useRef(null);

  const load = useCallback((q) => {
    setLoading(true);
    const params = new URLSearchParams({ page: 1, limit: 200 });
    if (q) params.set('search', q);
    api.get(`/customer/projects?${params}`, authHeaders())
      .then(res => {
        const data = res.data;
        setProjects(Array.isArray(data) ? data : (data.projects || []));
      })
      .catch(() => setError('Failed to load projects.'))
      .finally(() => setLoading(false));
  }, [authHeaders]);

  useEffect(() => { load(''); }, [load]);

  // Collect every pending approval that hasn't been dismissed this session.
  const computeOverdue = useCallback((projs, dismissedSet) => {
    const pending = [];
    projs.forEach(proj => {
      (proj.pending_approvals || []).forEach(pa => {
        if (dismissedSet.has(pa.notif_id)) return;
        pending.push({ ...pa, project_title: proj.title, job_code: proj.job_code });
      });
    });
    return pending;
  }, []);

  useEffect(() => {
    const check = () => {
      const overdue = computeOverdue(projects, dismissed);
      if (overdue.length > 0) { setOverdueList(overdue); setReminderOpen(true); }
    };
    check();
    reminderTimerRef.current = setInterval(check, 15 * 60 * 1000);
    return () => clearInterval(reminderTimerRef.current);
  }, [projects, dismissed, computeOverdue]);

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearch(val);
    setTabPage(1);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(val), 350);
  };

  const switchTab = (tab) => { setActiveTab(tab); setTabPage(1); };

  const openApprovalModal = (notifId, approvalType, projectTitle, jobCode, action) => {
    setApprovalModal({ notif_id: notifId, approval_type: approvalType, project_title: projectTitle, job_code: jobCode, action });
    setCorrectionFrom('Customer');
    setRemarks('');
    setModalError('');
  };

  const submitDecision = async () => {
    const isReject = approvalModal.action === 'reject';
    if (isReject && !remarks.trim()) { setModalError('Remarks are required when rejecting.'); return; }
    setModalLoading(true);
    try {
      const body = {
        decision: approvalModal.action,
        comment:  remarks.trim() || (approvalModal.action === 'approve' ? 'Approved by customer.' : 'Rejected by customer.'),
      };
      if (isReject) body.correction_from = correctionFrom;
      await api.post(`/customer/approvals/${approvalModal.notif_id}/decide`, body, authHeaders());
      setApprovalModal(null);
      load(search);
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to submit. Please try again.');
    } finally {
      setModalLoading(false);
    }
  };

  const dismissReminder = (notifId) => {
    setDismissed(prev => new Set([...prev, notifId]));
    setOverdueList(prev => prev.filter(x => x.notif_id !== notifId));
  };

  // Parse any date string (ISO or already-formatted) to dd/mm/yyyy
  const formatDateOnly = (dtStr) => {
    if (!dtStr) return '—';
    // Already formatted by backend: "27/05/2026, 14:30"
    if (/^\d{2}\/\d{2}\/\d{4}/.test(dtStr)) return dtStr.split(',')[0];
    // ISO string: "2026-05-27T05:56:44.046000Z" — convert to IST date
    try {
      const ms  = new Date(dtStr).getTime();
      const ist = new Date(ms + 5.5 * 60 * 60 * 1000);
      const d = String(ist.getUTCDate()).padStart(2, '0');
      const m = String(ist.getUTCMonth() + 1).padStart(2, '0');
      return `${d}/${m}/${ist.getUTCFullYear()}`;
    } catch { return dtStr; }
  };

  // Date + time, e.g. "27/05/2026, 14:30" (IST). Backend already returns this.
  const formatDateTime = (dtStr) => {
    if (!dtStr) return '—';
    if (/^\d{2}\/\d{2}\/\d{4}/.test(dtStr)) return dtStr; // backend pre-formatted with time
    try {
      const ms  = new Date(dtStr).getTime();
      const ist = new Date(ms + 5.5 * 60 * 60 * 1000);
      const d  = String(ist.getUTCDate()).padStart(2, '0');
      const m  = String(ist.getUTCMonth() + 1).padStart(2, '0');
      const hh = String(ist.getUTCHours()).padStart(2, '0');
      const mm = String(ist.getUTCMinutes()).padStart(2, '0');
      return `${d}/${m}/${ist.getUTCFullYear()}, ${hh}:${mm}`;
    } catch { return dtStr; }
  };

  const approvalProjects  = projects.filter(p => (p.pending_approvals || []).length > 0);
  // Client-approved jobs — shown until the project itself is completed.
  const approvedProjects  = projects.filter(p => (p.approved_approvals || []).length > 0 && p.status !== 'completed');
  // Jobs the client sent back. Kept visible while the job is still in progress,
  // so the customer can see their rejection is being worked on.
  const rejectedProjects  = projects.filter(p => (p.rejected_approvals || []).length > 0);
  // Finished jobs — these are the ones with despatch information.
  const despatchProjects  = projects.filter(p => !ACTIVE_SET.has(p.status) && !(p.pending_approvals || []).length);
  const currentList       = activeTab === 'approval' ? approvalProjects
                          : activeTab === 'approved' ? approvedProjects
                          : activeTab === 'rejected' ? rejectedProjects
                          : despatchProjects;
  const totalTabPages     = Math.max(1, Math.ceil(currentList.length / TAB_PAGE_SIZE));
  const pagedList         = currentList.slice((tabPage - 1) * TAB_PAGE_SIZE, tabPage * TAB_PAGE_SIZE);
  const fromRow           = currentList.length === 0 ? 0 : (tabPage - 1) * TAB_PAGE_SIZE + 1;
  const toRow             = Math.min(tabPage * TAB_PAGE_SIZE, currentList.length);

  const renderTable = (projs) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <th className="px-4 py-2.5 text-left whitespace-nowrap">Booking Date</th>
            <th className="px-4 py-2.5 text-left whitespace-nowrap">Job Code</th>
            <th className="px-4 py-2.5 text-left">Job Name</th>
            <th className="px-4 py-2.5 text-left whitespace-nowrap">Status</th>
            <th className="px-4 py-2.5 text-left whitespace-nowrap">Completed Date</th>
            <th className="px-4 py-2.5 text-left whitespace-nowrap">Approval</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {projs.map(proj => {
            const sd         = getProjectStatusDisplay(proj);
            const hasPending = (proj.pending_approvals || []).length > 0;
            return (
              <tr key={proj.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                  {formatDateTime(proj.created_at)}
                </td>
                <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap text-xs">
                  {proj.job_code || '—'}
                </td>
                <td className="px-4 py-3 text-gray-800 font-medium max-w-xs">
                  <span className="line-clamp-2">{proj.title}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                    style={{ backgroundColor: sd.color + '20', color: sd.color, border: `1px solid ${sd.color}50` }}
                  >
                    {sd.label}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                  {proj.status === 'completed' ? formatDateTime(proj.completed_at || proj.updated_at) : '—'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {activeTab === 'approved' ? (
                    (proj.approved_approvals || []).length > 0 ? (
                      <div className="flex flex-col gap-1.5">
                        {(proj.approved_approvals || []).map(aa => (
                          <div key={aa.notif_id} className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-gray-500">{aa.approval_type}</span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700">
                              <Check className="w-3 h-3" /> Approved
                            </span>
                            {aa.decided_at && <span className="text-[11px] text-gray-400">{aa.decided_at}</span>}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-300 text-xs">—</span>
                    )
                  ) : activeTab === 'rejected' ? (
                    (proj.rejected_approvals || []).length > 0 ? (
                      <div className="flex flex-col gap-1.5">
                        {(proj.rejected_approvals || []).map(ra => (
                          <div key={ra.notif_id} className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-gray-500">{ra.approval_type}</span>
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700">
                                <X className="w-3 h-3" /> Rejected
                              </span>
                              {ra.decided_at && <span className="text-[11px] text-gray-400">{ra.decided_at}</span>}
                            </div>
                            {/* The comment is why it was sent back — the most
                                useful thing on this row. */}
                            {ra.comment && (
                              <span className="text-[11px] text-gray-600 italic max-w-xs">
                                “{ra.comment}”
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-300 text-xs">—</span>
                    )
                  ) : hasPending ? (
                    <div className="flex flex-col gap-1.5">
                      {(proj.pending_approvals || []).map(pa => (
                        <div key={pa.notif_id} className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs text-gray-400 shrink-0">{pa.approval_type}</span>
                          <button
                            onClick={() => openApprovalModal(pa.notif_id, pa.approval_type, proj.title, proj.job_code, 'approve')}
                            className="px-2.5 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 flex items-center gap-1"
                          >
                            <Check className="w-3 h-3" /> Approve
                          </button>
                          <button
                            onClick={() => openApprovalModal(pa.notif_id, pa.approval_type, proj.title, proj.job_code, 'reject')}
                            className="px-2.5 py-1 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 flex items-center gap-1"
                          >
                            <X className="w-3 h-3" /> Reject
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-300 text-xs">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div>
      {/* Search + Refresh */}
      <div className="flex items-center gap-2 mb-5">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={handleSearch}
            placeholder="Search by title or job code…"
            className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={() => load(search)}
          className="p-2 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {loading && <div className="py-16 text-center text-gray-400">Loading projects…</div>}
      {error   && <div className="py-16 text-center text-red-500">{error}</div>}

      {!loading && !error && (
        <>
          {/* Tab switcher */}
          <div className="flex gap-1 mb-4 border-b border-gray-200">
            <button
              onClick={() => switchTab('approval')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition -mb-px ${
                activeTab === 'approval'
                  ? 'border-sky-600 text-sky-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Clock className="w-3.5 h-3.5 inline mr-1.5 text-sky-500" />
              For Approval
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${approvalProjects.length > 0 ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-600'}`}>
                {approvalProjects.length}
              </span>
            </button>
            <button
              onClick={() => switchTab('approved')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition -mb-px ${
                activeTab === 'approved'
                  ? 'border-green-600 text-green-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Check className="w-3.5 h-3.5 inline mr-1.5 text-green-500" />
              Approved
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${approvedProjects.length > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                {approvedProjects.length}
              </span>
            </button>
            <button
              onClick={() => switchTab('rejected')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition -mb-px ${
                activeTab === 'rejected'
                  ? 'border-red-600 text-red-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <X className="w-3.5 h-3.5 inline mr-1.5 text-red-500" />
              Rejected
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${rejectedProjects.length > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                {rejectedProjects.length}
              </span>
            </button>
            <button
              onClick={() => switchTab('despatch')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition -mb-px ${
                activeTab === 'despatch'
                  ? 'border-green-600 text-green-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <CheckCircle className="w-3.5 h-3.5 inline mr-1.5 text-green-500" />
              Despatch Details
              <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                {despatchProjects.length}
              </span>
            </button>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {currentList.length === 0 ? (
              <div className="py-16 text-center text-gray-400">
                {search
                  ? 'No matching jobs found.'
                  : activeTab === 'approval'
                    ? 'No jobs are waiting for your approval.'
                    : activeTab === 'approved'
                      ? 'No approved jobs yet.'
                      : activeTab === 'rejected'
                        ? 'You have not sent any jobs back.'
                        : 'No despatched jobs yet.'}
              </div>
            ) : (
              renderTable(pagedList)
            )}
          </div>

          {/* Pagination */}
          {totalTabPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
              <span className="text-xs text-gray-400">
                Showing {fromRow}–{toRow} of {currentList.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setTabPage(p => p - 1)}
                  disabled={tabPage <= 1}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                >← Prev</button>
                {Array.from({ length: totalTabPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalTabPages || Math.abs(p - tabPage) <= 1)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === '…' ? (
                      <span key={`el-${i}`} className="px-1 text-gray-400 text-sm">…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setTabPage(p)}
                        className={`w-8 h-8 rounded-lg text-sm font-medium ${
                          p === tabPage ? 'bg-blue-600 text-white' : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                        }`}
                      >{p}</button>
                    )
                  )
                }
                <button
                  onClick={() => setTabPage(p => p + 1)}
                  disabled={tabPage >= totalTabPages}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                >Next →</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Approve / Reject Modal ──────────────────────────────────────────── */}
      {approvalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {approvalModal.action === 'approve' ? '✓ Approve' : '✗ Reject'} — {approvalModal.approval_type}
              </h3>
              <button onClick={() => setApprovalModal(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              <span className="font-mono font-medium text-gray-700">{approvalModal.job_code}</span>
              {' — '}{approvalModal.project_title}
            </p>

            {modalError && (
              <div className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2 mb-4 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" /> {modalError}
              </div>
            )}

            {approvalModal.action === 'reject' && (
              <div className="mb-4">
                <label className="block text-xs font-semibold text-gray-600 mb-2">
                  Correction Required From <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  {['Customer', 'VP'].map(opt => (
                    <button
                      key={opt}
                      onClick={() => setCorrectionFrom(opt)}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium border transition ${
                        correctionFrom === opt
                          ? 'bg-red-600 text-white border-red-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                      }`}
                    >{opt}</button>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Remarks {approvalModal.action === 'reject' && <span className="text-red-500">*</span>}
              </label>
              <textarea
                rows={3}
                value={remarks}
                onChange={e => setRemarks(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder={approvalModal.action === 'reject' ? 'Describe the correction needed…' : 'Optional comment…'}
              />
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={() => setApprovalModal(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
              <button
                onClick={submitDecision}
                disabled={modalLoading}
                className={`px-4 py-2 rounded-lg text-sm text-white font-medium disabled:opacity-50 ${
                  approvalModal.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {modalLoading ? 'Submitting…' : approvalModal.action === 'approve' ? 'Submit Approval' : 'Submit Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Action Required — centered dialog ───────────────────────────────── */}
      {reminderOpen && overdueList.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-amber-200 w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 bg-amber-50 border-b border-amber-100">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <span className="text-base font-semibold text-amber-800">Action Required</span>
              </div>
              <button onClick={() => setReminderOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4">
              <p className="text-sm text-gray-600 mb-4">
                The following {overdueList.length === 1 ? 'job is' : 'jobs are'} waiting for your approval. Please review and decide.
              </p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {overdueList.map(pa => (
                  <div key={pa.notif_id} className="flex items-center justify-between gap-3 bg-amber-50 rounded-lg px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{pa.job_code} — {pa.project_title}</p>
                      <p className="text-xs text-gray-500 truncate">{pa.approval_type}</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button
                        onClick={() => { setReminderOpen(false); openApprovalModal(pa.notif_id, pa.approval_type, pa.project_title, pa.job_code, 'approve'); }}
                        className="px-2.5 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 flex items-center gap-1"
                      ><Check className="w-3 h-3" /> Approve</button>
                      <button
                        onClick={() => { setReminderOpen(false); openApprovalModal(pa.notif_id, pa.approval_type, pa.project_title, pa.job_code, 'reject'); }}
                        className="px-2.5 py-1 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 flex items-center gap-1"
                      ><X className="w-3 h-3" /> Reject</button>
                      <button
                        onClick={() => dismissReminder(pa.notif_id)}
                        className="text-xs text-gray-400 hover:text-gray-600 px-1.5 py-1 rounded hover:bg-gray-100 font-medium"
                        title="Dismiss for now"
                      >End</button>
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setReminderOpen(false)}
                className="mt-4 w-full py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600"
              >Remind me later</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Project Detail ────────────────────────────────────────────────────────────

function ProjectDetailView({ projectId, authHeaders, colors, onBack, onGoToNotifications }) {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedTask, setExpandedTask] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/customer/projects/${projectId}`, authHeaders())
      .then(res => setProject(res.data))
      .catch(() => setError('Failed to load project details.'))
      .finally(() => setLoading(false));
  }, [projectId, authHeaders]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="py-16 text-center text-gray-400">Loading…</div>;
  if (error) return <div className="py-16 text-center text-red-500">{error}</div>;
  if (!project) return null;

  const tasks = project.tasks || [];

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-blue-600 hover:underline mb-4">
        ← All Projects
      </button>

      {/* Project header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-6 py-5 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <span className="text-xs font-mono text-gray-400">{project.job_code}</span>
            <h2 className="text-xl font-bold text-gray-800 mt-0.5">{project.title}</h2>
            {project.client && <p className="text-sm text-gray-500 mt-1">{project.client}</p>}
          </div>
          <StatusBadge status={project.status} colors={colors} />
        </div>
        {project.description && <p className="mt-3 text-sm text-gray-600 border-t border-gray-100 pt-3">{project.description}</p>}
        <div className="mt-3 flex flex-wrap gap-4 text-xs text-gray-400">
          {project.created_at && <span>Booking: {project.created_at}</span>}
          {project.status === 'completed' && (project.completed_at || project.updated_at) && (
            <span>Completed: {project.completed_at || project.updated_at}</span>
          )}
          {project.updated_at && <span>Updated: {project.updated_at}</span>}
        </div>
      </div>

      {/* Task pipeline */}
      <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-3">Task Pipeline</h3>
      {tasks.length === 0 ? (
        <p className="text-sm text-gray-400">No tasks available.</p>
      ) : (
        <div className="space-y-2">
          {tasks.map((task, idx) => {
            const isExpanded = expandedTask === task.id;
            const isApproval = task.task_type === 'approval';
            return (
              <div key={task.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <button
                  onClick={() => setExpandedTask(isExpanded ? null : task.id)}
                  className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-50 transition"
                >
                  <span className="text-xs text-gray-300 font-mono w-6 text-center">{idx + 1}</span>
                  <span className="flex-shrink-0">{TASK_STATUS_ICON[task.status] || <Clock className="w-4 h-4 text-gray-400" />}</span>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-gray-800">{task.name}</span>
                    {isApproval && (
                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-purple-50 text-purple-700 border border-purple-200">
                        Approval
                      </span>
                    )}
                  </div>
                  <StatusBadge status={task.status} colors={colors} />
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                </button>

                {isExpanded && (
                  <div className="px-5 pb-4 border-t border-gray-100 bg-gray-50 text-sm text-gray-600 space-y-1.5">
                    {task.assigned_user_name && (
                      <div className="flex items-center gap-2">
                        <User className="w-3.5 h-3.5 text-gray-400" />
                        <span>Assigned to: <strong>{task.assigned_user_name}</strong></span>
                      </div>
                    )}
                    {task.started_at && <p className="text-xs text-gray-400">Started: {task.started_at}</p>}
                    {task.completed_at && <p className="text-xs text-gray-400">Completed: {task.completed_at}</p>}
                    {isApproval && task.approval_type && (
                      <p className="text-xs text-purple-600">Approval type: {task.approval_type}</p>
                    )}
                    {isApproval && task.customer_decision && (
                      <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${task.customer_decision === 'approved' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                        {task.customer_decision === 'approved' ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        {task.customer_decision === 'approved' ? 'Approved' : 'Rejected'}
                        {task.customer_decided_at && <span className="font-normal text-gray-400 ml-1">on {task.customer_decided_at}</span>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pending approvals CTA */}
      {tasks.some(t => t.task_type === 'approval' && t.status === 'in_progress' && !t.customer_decision) && (
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-amber-800 text-sm">
            <Clock className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <span>You have pending approval(s) waiting for your decision.</span>
          </div>
          <button onClick={onGoToNotifications} className="flex-shrink-0 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-medium hover:bg-amber-700">
            Go to Inbox
          </button>
        </div>
      )}
    </div>
  );
}

// ── Notifications Inbox ───────────────────────────────────────────────────────

function NotificationsView({ authHeaders, colors, onRefreshUnread }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvalModal, setApprovalModal] = useState(null); // { notif_id, title }
  const [decision, setDecision] = useState('approve');
  const [comment, setComment] = useState('');
  const [decisionError, setDecisionError] = useState('');
  const [decisionLoading, setDecisionLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/customer/notifications', authHeaders())
      .then(res => setNotifications(Array.isArray(res.data) ? res.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try {
      await api.put(`/customer/notifications/${id}/read`, {}, authHeaders());
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
      onRefreshUnread();
    } catch { /* ignore */ }
  };

  const openApproval = (notif) => {
    setApprovalModal(notif);
    setDecision('approve');
    setComment('');
    setDecisionError('');
  };

  const submitDecision = async () => {
    if (!comment.trim()) { setDecisionError('A comment is required.'); return; }
    setDecisionLoading(true);
    try {
      await api.post(
        `/customer/approvals/${approvalModal.approval_notif_id}/decide`,
        { decision, comment },
        authHeaders()
      );
      setApprovalModal(null);
      load();
      onRefreshUnread();
    } catch (err) {
      setDecisionError(err.response?.data?.error || 'Failed to submit decision.');
    } finally {
      setDecisionLoading(false);
    }
  };

  if (loading) return <div className="py-16 text-center text-gray-400">Loading notifications…</div>;

  const unread = notifications.filter(n => !n.read);
  const read = notifications.filter(n => n.read);

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Inbox className="w-5 h-5 text-gray-500" />
        <h2 className="text-xl font-bold text-gray-800">Inbox</h2>
        {unread.length > 0 && (
          <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">{unread.length} unread</span>
        )}
      </div>

      {notifications.length === 0 && (
        <div className="py-16 text-center text-gray-400">No notifications yet.</div>
      )}

      {[...unread, ...read].map(notif => {
        const isPendingApproval = notif.event_type === 'approval_waiting' && notif.approval_notif_id && !notif.decision_taken;
        return (
          <div
            key={notif._id}
            className={`bg-white rounded-xl border shadow-sm px-5 py-4 mb-3 transition ${!notif.read ? 'border-blue-200 bg-blue-50/30' : 'border-gray-200'}`}
            onClick={() => { if (!notif.read) markRead(notif._id); }}
          >
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex-shrink-0">{EVENT_ICONS[notif.event_type] || <Bell className="w-4 h-4 text-gray-400" />}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="text-sm font-medium text-gray-800">{notif.message}</p>
                  {!notif.read && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
                </div>
                {notif.project_title && (
                  <p className="text-xs text-gray-400">{notif.job_code} — {notif.project_title}</p>
                )}
                <p className="text-xs text-gray-300 mt-1">{notif.created_at}</p>
              </div>
              {isPendingApproval && (
                <button
                  onClick={e => { e.stopPropagation(); openApproval(notif); }}
                  className="flex-shrink-0 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700"
                >
                  Decide
                </button>
              )}
              {notif.event_type === 'approval_result' && notif.decision_taken && (
                <span className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${notif.decision_taken === 'approved' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {notif.decision_taken === 'approved' ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                  {notif.decision_taken}
                </span>
              )}
            </div>
          </div>
        );
      })}

      {/* Approval Decision Modal */}
      {approvalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Approval Decision</h3>
              <button onClick={() => setApprovalModal(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-gray-600 mb-4">{approvalModal.message}</p>

            {decisionError && (
              <div className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2 mb-3 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" /> {decisionError}
              </div>
            )}

            <div className="flex gap-3 mb-4">
              {['approve', 'reject'].map(opt => (
                <button
                  key={opt}
                  onClick={() => setDecision(opt)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition ${
                    decision === opt
                      ? opt === 'approve'
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-red-600 text-white border-red-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {opt === 'approve' ? '✓ Approve' : '✗ Reject'}
                </button>
              ))}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Comment <span className="text-red-500">*</span>
                <span className="text-gray-400 font-normal ml-1">(required)</span>
              </label>
              <textarea
                rows={3}
                value={comment}
                onChange={e => setComment(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="Add your comment or reason…"
              />
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setApprovalModal(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
              <button
                onClick={submitDecision}
                disabled={decisionLoading}
                className={`px-4 py-2 rounded-lg text-sm text-white font-medium disabled:opacity-50 ${decision === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
              >
                {decisionLoading ? 'Submitting…' : `Submit ${decision === 'approve' ? 'Approval' : 'Rejection'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
